import { useCallback, useEffect, useMemo, useState } from "react";

import {
  faArrowUpRightFromSquare,
  faExclamationCircle,
  faMagnifyingGlass,
  faRefresh,
  faSearch,
} from "@fortawesome/pro-regular-svg-icons";
import { useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { endOfToday, startOfToday, subDays } from "date-fns";
import { range } from "lodash-es";
import { DateRange, SelectRangeEventHandler } from "react-day-picker";
import { useSearchParams } from "react-router-dom";
import { twMerge } from "tailwind-merge";
import { useSessionStorage } from "usehooks-ts";
import { validate as isValidUUID } from "uuid";

import {
  DecisionHistoryRecordV3,
  DecisionHistoryV3QueryParams,
  DevisionHistoryV3GetRequest,
  decisionsV3BaseKey,
  NO_DECISION_FOUND,
  SingleDecisionEnvelope,
  useHistoryDecisionsV3,
  useHistoryDecisionV3,
} from "src/api/decisionHistoryV2/decisionHistoryQueries";
import { GroupFilter } from "src/api/endpoints";
import { Flow, FlowGroup, FlowGroupForCount } from "src/api/flowTypes";
import { useFlow, useFlows } from "src/api/queries";
import { DecisionEnvironment, DecisionSummaryResponse } from "src/api/types";
import { CollapsibleWidget } from "src/base-components/CollapsibleWidget";
import { CopyTextIcon } from "src/base-components/CopyTextIcon";
import { DateRangePicker } from "src/base-components/DateRangePicker";
import {
  EnvironmentPill,
  EnvironmentPillPlaceholder,
} from "src/base-components/EnvironmentPill";
import { FloatingWindowsProvider } from "src/base-components/FloatingWindow/FloatingWindowsProvider";
import { LoadingView } from "src/base-components/LoadingView";
import { useResizableSidepane } from "src/base-components/ResizableSidepane";
import {
  DecisionOriginType,
  HistoricalDecisionApiResponse,
} from "src/clients/history_v3";
import { ColumnSelector } from "src/decisionsOverview/ColumnSelector";
import { DecisionsSidepaneWrapper } from "src/decisionsOverview/DecisionsSidepaneWrapper";
import { LinkedFlowsPill } from "src/decisionsOverview/LinkedFlowsPill";
import { useDecisionsOverview } from "src/decisionsOverview/queries";
import {
  useDecisionEnv,
  useDetermineDecisionEnvironment,
} from "src/decisionsOverview/useDetermineDecisionEnvironment";
import { useViewOptions } from "src/decisionsOverview/useViewOptions";
import { emptyToUndefined } from "src/decisionsOverview/utils";
import { EmptyState } from "src/design-system/EmptyState";
import { IconButton } from "src/design-system/Icon";
import { Pill } from "src/design-system/Pill";
import { Skeleton } from "src/design-system/Skeleton";
import { Tooltip } from "src/design-system/Tooltip";
import { ManualReviewFilters } from "src/entities/entityView/DecisionsTab/ManualReviewStatusFilter";
import { TracingHoverBehavior } from "src/flow/decisionHistory/Columns";
import { DecisionHistoryTable } from "src/flow/decisionHistory/TableWrapper";
import { SubHeader } from "src/flow/SubHeader";
import { useCapabilities } from "src/hooks/useCapabilities";
import { readableEnvironments } from "src/hooks/useCapabilities/decisionEnvironments";
import { useIsIcebergDecisionSummaryEnabled } from "src/hooks/useIsIcebergDecisionSummaryEnabled";
import {
  tracker,
  trackingEvents,
} from "src/instrumentation/customTrackingEvents";
import { DashboardContent } from "src/layout/DashboardContent";
import { useOmniboxActions } from "src/omnibox/OmniboxStore";
import { useWorkspaceContext } from "src/router/routerContextHooks";
import { URLKeys } from "src/router/SearchParams";
import {
  DashboardPageParams,
  getBaseUrl,
  getFlowSubpageUrl,
  getUrlToWsDashboard,
} from "src/router/urls";
import { useCurrentUserId } from "src/store/AuthStore";
import { isFlowGroup, isFlowGroupForCount } from "src/utils/predicates";
import { pluralize } from "src/utils/stringUtils";
import { StrictDateRange, useTimeWindow } from "src/utils/timeWindow";
import { useParamsDecode } from "src/utils/useParamsDecode";

const sidepanePlaceholderPadding = 36;

export const useDecisionsOverviewQueryParams = () => {
  const [searchParams] = useSearchParams();

  return {
    entityId: searchParams.get(URLKeys.EntityId),
    decisionId: searchParams.get(URLKeys.DecisionId),
  };
};

type DecisionCountPillProps = {
  count: number | undefined;
  windowedCount?: number;
  isWindowedCountPartial?: boolean;
  isWindowedCountLoading?: boolean;
};

const DecisionCountPill: React.FC<DecisionCountPillProps> = ({
  count,
  windowedCount,
  isWindowedCountPartial,
  isWindowedCountLoading,
}) => {
  if (count === undefined || isWindowedCountLoading) {
    return (
      <Skeleton dataLoc="decisions-count-loading" height="h-5" width="w-24" />
    );
  }

  const showPartialCount = windowedCount !== undefined && windowedCount < count;

  return (
    <Tooltip
      disabled={!showPartialCount}
      title={
        isWindowedCountPartial
          ? "Scroll the table to load all decisions in the selected time range"
          : "There are decisions outside the selected time range"
      }
      asChild
    >
      <div>
        <Pill dataLoc={`decisions-count-${count}`} size="sm" variant="gray">
          <Pill.Text>
            {showPartialCount
              ? `${windowedCount}${isWindowedCountPartial ? "+" : ""} of ${pluralize(count, "Decision")}`
              : pluralize(count, "Decision")}
          </Pill.Text>
        </Pill>
      </div>
    </Tooltip>
  );
};

const RightContent: React.FC<
  DecisionCountPillProps & {
    flow: Flow;
    includeGroupingId: boolean;
  }
> = ({
  count,
  windowedCount,
  isWindowedCountPartial,
  isWindowedCountLoading,
  flow,
  includeGroupingId,
}) => {
  const { value, isSaving, canSelect, canDeselect, onChange, onReset, onSave } =
    useViewOptions({
      flow,
      includeGroupingId,
    });
  return (
    <div className="flex h-full items-center gap-x-2 pr-4">
      <DecisionCountPill
        count={count}
        isWindowedCountLoading={isWindowedCountLoading}
        isWindowedCountPartial={isWindowedCountPartial}
        windowedCount={windowedCount}
      />
      <ColumnSelector
        canDeselect={canDeselect}
        canSelect={canSelect}
        flow={flow}
        includeGroupingId={includeGroupingId}
        isSaving={isSaving}
        value={value}
        onChange={onChange}
        onReset={onReset}
        onSave={onSave}
      />
    </div>
  );
};

type ItemProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  flow: Flow;
  title: string;
  includeRelatedEntityDecisions?: boolean;
  manualReviewFilter?: ManualReviewFilters;
  count: number | undefined;
  selectedDecision: HistoricalDecisionApiResponse | null;
  singleDecision: HistoricalDecisionApiResponse | null;
  topLevelFilters: TopLevelFilters;
  filter: DevisionHistoryV3GetRequest | undefined;
  rowBehavior?: TracingHoverBehavior;
  withTimeWindowPicker?: boolean;
  onFilterChange: (tableFilter: DevisionHistoryV3GetRequest) => void;
  onSelectDecision: (decision: HistoricalDecisionApiResponse | null) => void;
};

const DEFAULT_TIME_WINDOW_DAYS = 7;

const buildQueryFilter = (
  baseUrl: string,
  flowId: string,
  filter: TopLevelFilters,
  fields: DevisionHistoryV3GetRequest | undefined,
): DecisionHistoryV3QueryParams => {
  return {
    baseUrl,
    ...(filter.entityId && {
      groupingIds: filter.entityId,
    }),
    ...(filter.decisionId &&
      isValidUUID(filter.decisionId) && {
        parentDecisionIds: filter.decisionId,
      }),
    ...fields,
    flowIds: flowId,
    environments: filter.decisionEnv,
    // Iceberg requires flow_ids and time range for partition pruning.
    // Use parent UUIDv7 timestamp when available, otherwise wide defaults.
    startStartTime:
      filter.timeWindow?.from.toISOString() ?? "2020-01-01T00:00:00Z",
    endStartTime: filter.timeWindow?.to.toISOString() ?? "2099-01-01T00:00:00Z",
  };
};

const FlowAccordionItem: React.FC<ItemProps> = ({
  isOpen,
  onOpenChange,
  includeRelatedEntityDecisions,
  manualReviewFilter,
  flow: flowShallow,
  title,
  count,
  topLevelFilters,
  filter,
  singleDecision,
  selectedDecision,
  rowBehavior,
  withTimeWindowPicker = false,
  onSelectDecision,
  onFilterChange,
}) => {
  const [cancellingDecisionIds, setCancellingDecisionIds] = useState<string[]>(
    [],
  );
  const userId = useCurrentUserId();
  const { orgId, wsId } = useParamsDecode<DashboardPageParams>();
  const flow = useFlow(flowShallow.id);
  const { columnVisibility, fieldsMask } = useViewOptions({
    flow: flow.data,
    includeCases: includeRelatedEntityDecisions,
    includeGroupingId: !includeRelatedEntityDecisions,
  });
  const { workspace } = useWorkspaceContext();
  const isIcebergDecisionSummaryEnabled = useIsIcebergDecisionSummaryEnabled();

  const { dateRangePickerValue, onDateRangePickerChange, timeWindow } =
    useTimeWindow<StrictDateRange | undefined>(
      withTimeWindowPicker
        ? {
            from: subDays(startOfToday(), DEFAULT_TIME_WINDOW_DAYS),
            to: endOfToday(),
          }
        : undefined,
      `decisions-accordion-time-window-${topLevelFilters.entityId ?? ""}-${flowShallow.id}`,
    );

  const contentFilters: TopLevelFilters = withTimeWindowPicker
    ? {
        ...topLevelFilters,
        timeWindow: timeWindow ?? topLevelFilters.timeWindow,
      }
    : topLevelFilters;

  const decisions = useHistoryDecisionsV3({
    ...buildQueryFilter(
      workspace?.base_url ?? "",
      flowShallow?.id ?? "",
      contentFilters,
      filter,
    ),
    groupingIds: includeRelatedEntityDecisions
      ? undefined
      : (topLevelFilters.entityId ?? undefined),
    relatedEntityIds: includeRelatedEntityDecisions
      ? (topLevelFilters.entityId ?? undefined)
      : undefined,
    enabled: isOpen && flowShallow?.slug !== undefined,
    caseAssignees:
      manualReviewFilter === ManualReviewFilters.ASSIGNED_TO_ME
        ? userId
        : undefined,
    caseStatus:
      manualReviewFilter === ManualReviewFilters.NEEDS_REVIEW
        ? "needs_review,in_progress"
        : undefined,
    $fields: fieldsMask,
    // Use the same iceberg flag as the decision summary query to ensure
    // consistency between counts and list results on the entity decisions page.
    // Without this, the summary may read from Iceberg while the list reads from
    // Aurora, causing timeouts for entities with many decisions (OPTI-5612).
    ...(includeRelatedEntityDecisions && {
      icebergOverride: isIcebergDecisionSummaryEnabled,
    }),
  });

  const handleTimeWindowChange: SelectRangeEventHandler = (
    range: DateRange | undefined,
  ) => {
    if (range?.from && range?.to) {
      onDateRangePickerChange(range);
    }
  };

  const decisionHistory = singleDecision
    ? [singleDecision]
    : decisions.data?.pages.flatMap((page) => page.decisions);

  // The loaded rows are an exact windowed total once the last page is in, and a
  // lower bound before that. Expose both so the pill can say "N+" rather than
  // silently falling back to the all-time count while pages remain.
  const windowedCount =
    withTimeWindowPicker && !singleDecision && decisions.data !== undefined
      ? decisionHistory?.length
      : undefined;
  const isWindowedCountPartial =
    windowedCount !== undefined && decisions.hasNextPage;
  // A collapsed accordion never fetches rows, so its query stays disabled and
  // reports isLoading false — the pill falls back to the all-time count there
  // rather than skeletoning forever.
  const isWindowedCountLoading =
    withTimeWindowPicker && !singleDecision && decisions.isLoading;

  if (!flow.data) return <AccordionSkeletonItem key={flowShallow?.id} />;

  const linkToFlow =
    getBaseUrl() + getFlowSubpageUrl(orgId, wsId, flow.data.id, "history");

  const renderContent = () => {
    if (decisions.isError) {
      const isTimeout =
        isAxiosError(decisions.error) &&
        decisions.error.response?.status === 408;
      return isTimeout ? (
        <EmptyState
          dataLoc="decisions-timeout-error-state"
          description="To load decisions faster, select a shorter time window"
          headline="Request timed out"
          icon={faExclamationCircle}
          variant="error"
        />
      ) : (
        <EmptyState
          actionButton={{
            label: "Retry",
            icon: faRefresh,
            onClick: () => decisions.refetch(),
          }}
          dataLoc="decisions-error-state"
          description="To view decisions for this flow, try again"
          headline="Error loading decisions"
          icon={faExclamationCircle}
          variant="error"
        />
      );
    }

    if (
      withTimeWindowPicker &&
      !singleDecision &&
      decisionHistory?.length === 0
    ) {
      return (
        <EmptyState
          dataLoc="decisions-window-empty-state"
          description="Adjust the time window to view more decisions"
          headline="No decisions in the selected period"
          icon={faSearch}
        />
      );
    }

    return (
      <div className="decideScrollbar mx-4 overflow-hidden overflow-x-auto pt-4 pr-0 pl-0">
        <div className="relative mx-auto flex max-h-[640px]">
          <FloatingWindowsProvider>
            <DecisionHistoryTable
              canFetchNextPage={
                !singleDecision &&
                Boolean(!decisions.isFetching && decisions.hasNextPage)
              }
              cancellingDecisionIds={cancellingDecisionIds}
              changeFilterFields={onFilterChange}
              columnVisibility={columnVisibility}
              decisionHistory={decisionHistory}
              env={topLevelFilters.decisionEnv}
              fetchNextPage={decisions.fetchNextPage}
              filterFields={filter || {}}
              flow={flow.data!}
              includeGroupingId={!includeRelatedEntityDecisions}
              isFetching={!singleDecision && decisions.isLoading}
              selectedDecisionId={
                selectedDecision ? selectedDecision.id : undefined
              }
              setCancellingDecisionIds={setCancellingDecisionIds}
              tracingHoverBehavior={rowBehavior}
              includeInputOutput
              includeOutcomes
              onDecisionClick={{
                predicate: () => true,
                effect: onSelectDecision,
              }}
            />
          </FloatingWindowsProvider>
        </div>
      </div>
    );
  };

  return (
    <CollapsibleWidget
      dataLoc={`accordion-item-${flow.data.name}`}
      isOpen={isOpen}
      rightSlot={
        <div className="flex h-full items-center">
          {withTimeWindowPicker && (
            <DateRangePicker
              value={dateRangePickerValue}
              borderless
              suffixIcon
              usePredefinedDates
              onChange={handleTimeWindowChange}
            />
          )}
          <RightContent
            count={count}
            flow={flow.data}
            includeGroupingId={!includeRelatedEntityDecisions}
            isWindowedCountLoading={isWindowedCountLoading}
            isWindowedCountPartial={isWindowedCountPartial}
            windowedCount={windowedCount}
          />
        </div>
      }
      title={
        <div
          className="flex items-center gap-x-1.5"
          data-loc="accordion-item-title"
        >
          <Tooltip title={title} asChild>
            <span className="line-clamp-2 min-w-24 text-left">{title}</span>
          </Tooltip>
          <Tooltip placement="right" title="Open in new tab" asChild>
            <div>
              <IconButton
                icon={faArrowUpRightFromSquare}
                size="2xs"
                onClick={(event) => {
                  event.stopPropagation();
                  window.open(linkToFlow, "_blank");
                }}
              />
            </div>
          </Tooltip>
          <LinkedFlowsPill
            childFlows={flowShallow.child_flows}
            parentFlows={flowShallow.parent_flows}
          />
        </div>
      }
      onOpenChange={onOpenChange}
    >
      {renderContent()}
    </CollapsibleWidget>
  );
};

type Props = {
  singleDecision: HistoricalDecisionApiResponse | null;
  includeRelatedEntityDecisions?: boolean;
  manualReviewFilter?: ManualReviewFilters;
  flowGroups: FlowGroup[];
  flowGroupsForCount: FlowGroupForCount[] | undefined;
  selectedDecision: HistoricalDecisionApiResponse | null;
  topLevelFilters: TopLevelFilters;
  groupFilter: GroupFilter;
  rowBehavior?: TracingHoverBehavior;
  withTimeWindowPicker?: boolean;
  onGroupFilterChange: React.Dispatch<React.SetStateAction<GroupFilter>>;
  onSelectDecision: (decision: HistoricalDecisionApiResponse | null) => void;
};

export const FlowAccordion: React.FC<Props> = ({
  singleDecision,
  flowGroups,
  flowGroupsForCount,
  topLevelFilters,
  selectedDecision,
  groupFilter,
  rowBehavior,
  includeRelatedEntityDecisions,
  manualReviewFilter,
  withTimeWindowPicker,
  onGroupFilterChange,
  onSelectDecision,
}) => {
  const { decisionId: urlDecisionId, entityId: urlEntityId } =
    useDecisionsOverviewQueryParams();
  const [accordionValue, setAccordionValue] = useSessionStorage<string[]>(
    `decisions-overview-accordion-search-id-${urlDecisionId ?? urlEntityId}`,
    [],
  );
  const [hasSetInitialValue, setHasSetInitialValue] =
    useSessionStorage<boolean>(
      `decisions-overview-accordion-initial-set-${urlDecisionId ?? urlEntityId}`,
      false,
    );

  useEffect(() => {
    onSelectDecision(null);
  }, [topLevelFilters, groupFilter, onSelectDecision]);

  useEffect(() => {
    if (flowGroups.length > 0 && !hasSetInitialValue) {
      setAccordionValue([flowGroups[0].flow.id]);
      setHasSetInitialValue(true);
    }
  }, [
    flowGroups,
    hasSetInitialValue,
    setAccordionValue,
    setHasSetInitialValue,
  ]);

  const handleAccordionChange = (value: string[]) => {
    if (
      selectedDecision?.flow?.id &&
      !value.includes(selectedDecision.flow.id)
    ) {
      onSelectDecision(null);
    }
    setAccordionValue(value);
  };

  return (
    <div className="flex flex-col gap-y-3 pb-6">
      {flowGroups.map((group) => (
        <FlowAccordionItem
          key={group.flow.id}
          count={
            flowGroupsForCount
              ? (flowGroupsForCount.find(
                  (flowGroup) => flowGroup.flowId === group.flow.id,
                )?.count ?? 0)
              : undefined
          }
          filter={groupFilter[group.flow.id]}
          flow={group.flow}
          includeRelatedEntityDecisions={includeRelatedEntityDecisions}
          isOpen={accordionValue.includes(group.flow.id)}
          manualReviewFilter={manualReviewFilter}
          rowBehavior={rowBehavior}
          selectedDecision={selectedDecision}
          singleDecision={
            singleDecision?.flow?.id === group.flow.id ? singleDecision : null
          }
          title={group.flow.name}
          topLevelFilters={topLevelFilters}
          withTimeWindowPicker={withTimeWindowPicker}
          onFilterChange={(tableFilter: DevisionHistoryV3GetRequest) => {
            onGroupFilterChange((prevGroupFilter) => ({
              ...prevGroupFilter,
              [group.flow.id]: {
                ...tableFilter,
                statusCodes: tableFilter.statusCodes ?? undefined,
                flowVersionIds: tableFilter.flowVersionIds ?? undefined,
                originTypes:
                  (tableFilter.originTypes as DecisionOriginType) ?? undefined,
                parentFlowIds: tableFilter.parentFlowIds ?? undefined,
                parentFlowVersionIds:
                  tableFilter.parentFlowVersionIds ?? undefined,
                jobIds: tableFilter.jobIds ?? undefined,
                jobRunIds: tableFilter.jobRunIds ?? undefined,
              },
            }));
          }}
          onOpenChange={(open) => {
            const newValue = open
              ? [...accordionValue, group.flow.id]
              : accordionValue.filter((id) => id !== group.flow.id);
            handleAccordionChange(newValue);
          }}
          onSelectDecision={onSelectDecision}
        />
      ))}
    </div>
  );
};

export type TopLevelFilters = {
  decisionEnv: DecisionEnvironment;
  entityId: string | null;
  decisionId: string | null;
  timeWindow: StrictDateRange | undefined;
};

const AccordionSkeletonItem: React.FC = () => {
  return (
    <CollapsibleWidget title={<Skeleton width="w-96" />}>
      <></>
    </CollapsibleWidget>
  );
};

export const AccordionSkeleton: React.FC = () => {
  return (
    <div className="flex flex-col gap-y-3">
      {range(4).map((index) => (
        <AccordionSkeletonItem key={index} />
      ))}
    </div>
  );
};

export const DecisionsOverview: React.FC = () => {
  const { orgId, wsId } = useParamsDecode<DashboardPageParams>();
  const { decisionHistory } = useCapabilities();
  const readableEnvs = readableEnvironments(decisionHistory);
  const canReadLive = readableEnvs.includes(DecisionEnvironment.LIVE);
  const canReadSandbox = readableEnvs.includes(DecisionEnvironment.SANDBOX);
  const { showOmnibox } = useOmniboxActions();
  const { workspace } = useWorkspaceContext();
  const { entityId, decisionId } = useDecisionsOverviewQueryParams();

  const {
    decisionEnvParam,
    decisionEnvForQueries,
    confirmedNoLiveDecisions,
    setDecisionEnvParam,
    setConfirmedNoLiveDecisions,
  } = useDecisionEnv(canReadLive, canReadSandbox);

  const [selectedDecision, state_setSelectedDecision] =
    useState<HistoricalDecisionApiResponse | null>(null);

  const setSelectedDecision = useCallback(
    (decision: HistoricalDecisionApiResponse | null) => {
      state_setSelectedDecision(decision);
      if (decision !== null) {
        tracker.emit(
          trackingEvents.openDecisionDetails({
            query_param_decision_id: decisionId,
            query_param_entity_id: entityId,
            decision_id: decision.id ?? "",
            organization_id: orgId,
            env: decisionEnvParam,
          }),
        );
      }
    },
    [decisionEnvParam, decisionId, entityId, orgId],
  );

  const {
    dateRangePickerValue,
    onDateRangePickerChange,
    onDateRangePickerReset,
    timeWindow,
  } = useTimeWindow<StrictDateRange | undefined>(
    undefined,
    "decisions-overview-time-window",
  );

  const topLevelFilters: TopLevelFilters = useMemo(() => {
    return {
      decisionEnv: decisionEnvForQueries,
      entityId: entityId,
      decisionId: decisionId,
      timeWindow,
    };
  }, [decisionEnvForQueries, entityId, decisionId, timeWindow]);

  const [groupFilter, setGroupFilter] = useSessionStorage<GroupFilter>(
    "decisions-overview-group-filter",
    {},
  );
  const queryClient = useQueryClient();

  const decisionsOverviewQuery = useDecisionsOverview(workspace.base_url, {
    environments: decisionEnvForQueries,
    groupby: "flow.id",
    aggregate: "count",
    grouping_id: emptyToUndefined(entityId),
    parent_decision_id: emptyToUndefined(decisionId),
    timeWindow: timeWindow,
  });

  const decisionsOverviewQueryForCount = useDecisionsOverview(
    workspace.base_url,
    {
      environments: decisionEnvForQueries,
      groupby: "flow.id",
      aggregate: "count",
      grouping_id: emptyToUndefined(entityId),
      parent_decision_id: emptyToUndefined(decisionId),
      timeWindow: timeWindow,
      groupfilter: groupFilter,
    },
  );

  const singleDecisionQuery = useHistoryDecisionV3({
    baseUrl: workspace.base_url ?? "",
    decisionId: decisionId ?? "",
    environmentToMatch: decisionEnvForQueries,
    // We give an initial value for the deactivated state so that the loading view treats this as loaded.
    initialData: decisionId
      ? undefined
      : { isQueryError: true, reason: NO_DECISION_FOUND },
  });

  const getDatePickerValue = (): DateRange | undefined => {
    if (!decisionId) {
      return dateRangePickerValue;
    }

    if (
      !singleDecisionQuery.data?.isQueryError &&
      singleDecisionQuery.data?.decision?.start_time
    ) {
      return {
        from: new Date(singleDecisionQuery?.data?.decision?.start_time),
      };
    }

    return undefined;
  };

  const flowsQuery = useFlows({ workspaceId: wsId });

  const flowGroupsForCount: FlowGroupForCount[] | undefined = useMemo(() => {
    const groups = decisionsOverviewQueryForCount.data?.groups
      .map((group) => ({
        flowId: group.flow?.id,
        count: group.count,
      }))
      .filter(isFlowGroupForCount);
    const singleDecision = singleDecisionQuery.data;
    if (
      singleDecision &&
      !singleDecision.isQueryError &&
      singleDecision.decision.flow?.id
    ) {
      const existingGroup = groups?.find(
        (group) => group.flowId === singleDecision.decision.flow?.id,
      );
      if (existingGroup) {
        existingGroup.count += 1;
      } else {
        groups?.push({
          flowId: singleDecision.decision.flow.id,
          count: 1,
        });
      }
    }
    return groups;
  }, [decisionsOverviewQueryForCount.data, singleDecisionQuery.data]);

  const handleRefresh = () => {
    decisionsOverviewQuery.refetch();
    decisionsOverviewQueryForCount.refetch();
    queryClient.refetchQueries({ queryKey: decisionsV3BaseKey });
  };

  useDetermineDecisionEnvironment({
    canReadLive,
    decisionEnvForQueries,
    decisionEnvParam,
    entityId,
    decisionsOverview: decisionsOverviewQuery.data,
    setDecisionEnvParam,
    decisionId,
    singleDecision: singleDecisionQuery.data,
    confirmedNoLiveDecisions,
    setConfirmedNoLiveDecisions,
  });
  const { width, isResizing } = useResizableSidepane();

  const getTitle = () => {
    if (decisionId) {
      return "Decision ID";
    }

    if (entityId) {
      return "Decisions for Grouping ID";
    }

    return "Decisions";
  };

  return (
    <DashboardContent
      Header={
        <SubHeader
          backTo={getUrlToWsDashboard({ orgId, wsId })}
          title={
            <>
              <span className="mr-2 whitespace-nowrap align-middle">
                {getTitle()}
              </span>
              <div className="mb-0.5 inline-block h-fit max-w-60 items-center overflow-hidden align-middle">
                <Pill
                  fontType="code"
                  dataLoc="decisions-overview-id-pill"
                  size="sm"
                  variant="gray"
                  maxWidth
                >
                  <Pill.Text>{decisionId ?? entityId ?? "-"}</Pill.Text>
                </Pill>
              </div>
            </>
          }
          titleAction={
            <>
              <CopyTextIcon
                tooltip="Copy ID"
                value={decisionId ?? entityId ?? ""}
              />
              {decisionEnvParam === null ? (
                <EnvironmentPillPlaceholder />
              ) : (
                <EnvironmentPill
                  environments={readableEnvs}
                  value={decisionEnvParam}
                  onChange={setDecisionEnvParam}
                />
              )}
            </>
          }
        >
          <SubHeader.Button
            dataLoc="header-search"
            icon={faMagnifyingGlass}
            tooltip="Search"
            onClick={showOmnibox}
          />
          <SubHeader.DatePicker
            disabled={Boolean(decisionId)}
            placeholder="All time"
            value={getDatePickerValue()}
            resetable
            onChange={onDateRangePickerChange}
            onReset={onDateRangePickerReset}
          />
          <SubHeader.Button
            dataLoc="refresh-decisions"
            disabled={decisionsOverviewQuery.isRefetching}
            icon={faRefresh}
            spin={decisionsOverviewQuery.isRefetching}
            tooltip="Refresh"
            onClick={handleRefresh}
          />
        </SubHeader>
      }
    >
      <div
        className={twMerge(
          "flex h-full w-full",
          isResizing && "cursor-col-resize",
        )}
      >
        <div className="mx-7 mb-10 h-fit min-w-[600px] grow overflow-hidden">
          <LoadingView
            queryResult={[
              decisionsOverviewQuery,
              flowsQuery,
              singleDecisionQuery,
            ]}
            renderUpdated={([decisionSummary, flows, singleDecision]: [
              DecisionSummaryResponse,
              Flow[],
              SingleDecisionEnvelope<DecisionHistoryRecordV3>,
            ]) => {
              const flowGroups: FlowGroup[] =
                decisionSummary.groups
                  .map((group) => ({
                    flow: flows.find((flow) => flow.id === group.flow?.id),
                  }))
                  .filter(isFlowGroup) ?? [];
              if (
                !singleDecision.isQueryError &&
                !flowGroups.find(
                  (group) =>
                    group.flow?.id === singleDecision.decision.flow?.id,
                )
              ) {
                const singleDecisionFlow = flows.find(
                  (flow) => flow.id === singleDecision.decision.flow?.id,
                );
                if (singleDecisionFlow) {
                  flowGroups.unshift({
                    flow: singleDecisionFlow,
                  });
                }
              }
              if (flowGroups.length === 0) {
                return (
                  <div className="h-[calc(100vh-120px)]">
                    <EmptyState
                      description="Try adjusting the search or filter to find what you are looking for"
                      headline="No results found"
                      icon={faSearch}
                    />
                  </div>
                );
              }
              return (
                <FlowAccordion
                  flowGroups={flowGroups.sort((a, b) =>
                    a.flow?.name.localeCompare(b.flow?.name),
                  )}
                  flowGroupsForCount={flowGroupsForCount}
                  groupFilter={groupFilter}
                  selectedDecision={selectedDecision}
                  singleDecision={
                    singleDecision.isQueryError ? null : singleDecision.decision
                  }
                  topLevelFilters={topLevelFilters}
                  onGroupFilterChange={setGroupFilter}
                  onSelectDecision={setSelectedDecision}
                />
              );
            }}
            renderUpdating={() => <AccordionSkeleton />}
          />
        </div>
        {/*Make some space for the sidepane to take, but allow it to overlap the
        table on small screens. */}
        {!!selectedDecision && (
          <div style={{ width: width - sidepanePlaceholderPadding }} />
        )}
        <DecisionsSidepaneWrapper
          baseUrl={workspace.base_url!}
          decision={selectedDecision}
          isOpen={!!selectedDecision}
          onClose={() => setSelectedDecision(null)}
        />
      </div>
      <div className="h-14" />
    </DashboardContent>
  );
};
